import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Stack,
    Avatar,
    Divider,
    useTheme
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import { useAuth } from '../../contexts/AuthContext';
import DuelSyncSettings from '../Tracker/DuelSyncSettings';

function UserSettings() {
    const theme = useTheme();
    const { currentUser, userData, updateDisplayName, loading } = useAuth();
    const [displayName, setDisplayName] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Inicjalizuj display_name gdy dane użytkownika się załadują
    useEffect(() => {
        if (userData?.display_name) {
            setDisplayName(userData.display_name);
        } else if (currentUser?.displayName) {
            setDisplayName(currentUser.displayName);
        } else if (currentUser?.email) {
            setDisplayName(currentUser.email.split('@')[0]);
        }
    }, [userData, currentUser]);

    const handleSave = async () => {
        if (!displayName.trim()) {
            setMessage({ type: 'error', text: 'Nazwa wyświetlana nie może być pusta' });
            return;
        }

        if (displayName.trim().length > 50) {
            setMessage({ type: 'error', text: 'Nazwa wyświetlana nie może być dłuższa niż 50 znaków' });
            return;
        }

        setIsSaving(true);
        setMessage({ type: '', text: '' });

        try {
            await updateDisplayName(displayName.trim());
            setMessage({ type: 'success', text: 'Nazwa wyświetlana została zaktualizowana' });
        } catch (error) {
            console.error('Błąd podczas aktualizacji nazwy wyświetlanej:', error);
            setMessage({ type: 'error', text: 'Wystąpił błąd podczas zapisywania. Spróbuj ponownie.' });
        } finally {
            setIsSaving(false);
        }
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    if (loading) {
        return (
            <Container maxWidth="sm">
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '50vh',
                    gap: 2
                }}>
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary">
                        Ładowanie danych użytkownika...
                    </Typography>
                </Box>
            </Container>
        );
    }

    if (!currentUser) {
        return (
            <Container maxWidth="sm">
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '50vh',
                    gap: 2
                }}>
                    <Typography variant="h6" color="text.secondary">
                        Musisz być zalogowany, aby edytować ustawienia
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm">
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mt: { xs: 4, sm: 8 },
                mb: 4
            }}>
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        width: '100%',
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 3
                    }}
                >
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography variant="h4" component="h1" gutterBottom>
                            Ustawienia konta
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Zarządzaj swoimi danymi i preferencjami
                        </Typography>
                    </Box>

                    {/* Informacje o użytkowniku */}
                    <Box sx={{ mb: 3 }}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                            <Avatar
                                src={currentUser.photoURL}
                                sx={{
                                    width: 60,
                                    height: 60,
                                    backgroundColor: theme.palette.primary.main
                                }}
                            >
                                {!currentUser.photoURL && getInitials(displayName)}
                            </Avatar>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {userData?.display_name || currentUser.displayName || 'Użytkownik'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {currentUser.email}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Rola: {userData?.role || 'user'}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* Formularz edycji */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon />
                            Nazwa wyświetlana
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Ta nazwa będzie wyświetlana w aplikacji zamiast imienia i nazwiska z Google.
                        </Typography>

                        <TextField
                            fullWidth
                            label="Nazwa wyświetlana"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            variant="outlined"
                            placeholder="Wprowadź swoją nazwę wyświetlaną"
                            disabled={isSaving}
                            sx={{ mb: 2 }}
                            helperText={`${displayName.length}/50 znaków`}
                            inputProps={{ maxLength: 50 }}
                        />

                        {message.text && (
                            <Alert
                                severity={message.type}
                                sx={{ mb: 2 }}
                                onClose={() => setMessage({ type: '', text: '' })}
                            >
                                {message.text}
                            </Alert>
                        )}

                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={isSaving || !displayName.trim()}
                            startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                            fullWidth
                            sx={{
                                py: 1.5,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontSize: '1rem',
                                fontWeight: 600
                            }}
                        >
                            {isSaving ? 'Zapisywanie...' : 'Zapisz zmiany'}
                        </Button>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* duels.ink */}
                    <DuelSyncSettings />

                    <Divider sx={{ my: 3 }} />

                    {/* Informacje dodatkowe */}
                    <Box sx={{
                        p: 2,
                        backgroundColor: theme.palette.background.default,
                        borderRadius: 2,
                        textAlign: 'center'
                    }}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Informacje o koncie</strong><br />
                            Twoje dane są bezpiecznie przechowywane w bazie danych Firebase.
                            Możesz w każdej chwili zmienić swoją nazwę wyświetlaną.
                        </Typography>
                    </Box>
                </Paper>
            </Box>
        </Container>
    );
}

export default UserSettings;
